import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card } from '../components/ui/Card';
import { Table } from '../components/ui/Table';
import { Badge } from '../components/ui/Badge';
import { apiList } from '../api/apiClient';
import { formatDate } from '../utils/formatters';
import { Phone, Clock, Unlock, TrendingUp, Download, RefreshCw, Search } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { isAdmin } from '../utils/roles';
import { CountBand } from '../components/ui/CountBand';

/** V4: Visitor phone numbers are PII. Non-admin staff (PM, dealer, customer)
 *  see them masked; only super_admin / admin see raw numbers. */
const maskVisitorPhone = (raw: string): string => {
  if (!raw) return raw;
  const digits = String(raw).replace(/\D/g, '');
  if (digits.length < 7) return '•••';
  return `•••-•••-${digits.slice(-4)}`;
};

type DateFilter = 'today' | 'week' | 'month' | 'all';
// "No Answer" is treated as Missed — there's no functional difference between
// the two from the property manager's perspective.
type StatusFilter = 'all' | 'answered' | 'missed';
type TypeFilter = 'all' | 'voice' | 'video' | 'kiosk';

interface CallHistoryProps {
  /** When set, the property filter is locked to this id (and the picker
   *  + page header are hidden). Used to embed this view in
   *  PropertyDetail so the per-property call history matches the global
   *  call history exactly. */
  presetPropertyId?: string;
  /** When set, only calls for this tenant are loaded — used to embed
   *  the same view inside the per-tenant detail page. */
  presetTenantId?: string;
  hidePageHeader?: boolean;
}

export const CallHistory: React.FC<CallHistoryProps> = ({ presetPropertyId, presetTenantId, hidePageHeader }) => {
  const { user } = useAuth();
  const canSeeFullPhone = isAdmin((user?.role as any) || '');
  const [calls, setCalls] = useState<any[]>([]);
  const [properties, setProperties] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [propertyFilter, setPropertyFilter] = useState<string | 'all'>(presetPropertyId ?? 'all');
  const [dateFilter, setDateFilter] = useState<DateFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [tenantFilter, setTenantFilter] = useState<string | 'all'>('all');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const load = useCallback(async () => {
    try {
      // When scoped to a tenant, hit /events/calls?tenant_id directly
      // so the row set is filtered server-side rather than over-fetching.
      const callPromise = presetTenantId
        ? (async () => {
            const API = process.env.REACT_APP_API_URL || 'https://api.intrlock.io';
            const token = localStorage.getItem('intrlock_token') || localStorage.getItem('jwt') || localStorage.getItem('token') || '';
            const res = await fetch(`${API}/events/calls?tenant_id=${presetTenantId}&limit=500`, {
              headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            if (!res.ok) return [];
            const j = await res.json();
            return Array.isArray(j) ? j : (j.items || j.calls || []);
          })().catch(() => [])
        : apiList('call_history').catch(() => []);
      const [callData, propertyData] = await Promise.all([
        callPromise,
        apiList('properties').catch(() => []),
      ]);

      const propMap: Record<string, any> = {};
      (propertyData || []).forEach((p: any) => {
        propMap[String(p.id)] = p;
        if (p.uuid) propMap[p.uuid] = p;
      });
      setProperties(propMap);

      // Defense-in-depth: drop any call whose property is outside the
      // logged-in user's scope. Server already does this, but a stale
      // DDB-historical row could otherwise leak through.
      const allowedSet = new Set<string>();
      (propertyData || []).forEach((p: any) => {
        if (p.id) allowedSet.add(String(p.id));
        if (p.uuid) allowedSet.add(String(p.uuid));
        if (p.legacy_id) allowedSet.add(String(p.legacy_id));
      });
      const scopedCalls = allowedSet.size === 0
        ? (callData || [])
        : (callData || []).filter((c: any) =>
            !c.property_id || allowedSet.has(String(c.property_id))
          );
      const enriched = scopedCalls.map((call: any) => {
        const property = propMap[String(call.property_id)] || {};
        // Compute the TENANT TALK duration when the row has answered_at:
        // a Telnyx-bridged call has a visitor leg (full wall time —
        // captured in `duration`) and a tenant leg (post-answer talk
        // time — derived from ended_at - answered_at).
        let tenantTalkSecs = 0;
        if (call.answered_at) {
          const ans = new Date(call.answered_at).getTime();
          const end = call.ended_at ? new Date(call.ended_at).getTime() : null;
          if (end && end > ans) tenantTalkSecs = Math.round((end - ans) / 1000);
        }
        return {
          ...call,
          property_name: property.name || `Property #${call.property_id || '?'}`,
          unit_number: call.unit_number || call.resolved_unit_number || '—',
          tenant_name: (call.tenant_name || '').trim() || '—',
          caller: call.visitor_name
            || (call.from_number ? (canSeeFullPhone ? call.from_number : maskVisitorPhone(call.from_number)) : '—'),
          duration: Number(call.duration || call.duration_seconds || 0),
          tenant_talk_duration: tenantTalkSecs,
          call_time: call.created_at || call.call_time,
          call_type: call.call_type === 'intercom' ? 'voice' : (call.call_type || 'voice'),
          connected: ['answered', 'completed', 'unlocked'].includes((call.status || '').toLowerCase()) || call.connected === true,
        };
      });

      setCalls(enriched);
    } catch (e) {
      console.error('Failed to load call history:', e);
    } finally {
      setLoading(false);
    }
  }, [presetTenantId, canSeeFullPhone]);

  useEffect(() => { load(); }, [load]);

  /**
   * Voice (Telnyx) calls produce TWO call_event rows per connected call —
   * one for the visitor leg and one for the tenant leg. Group consecutive
   * voice rows that share property_id + from_number within a 90s window
   * into a single "merged" item. The merged item carries:
   *  - duration: sum of both legs (so total minutes is correct)
   *  - legs: array of original rows so the expanded view can show each
   *  - status: most "successful" of the legs (answered > completed > missed)
   *  - connected: true if any leg connected
   *
   * Video calls are passed through 1:1 since LiveKit produces a single row.
   */
  const mergeVoiceLegs = (rows: any[]): any[] => {
    const sorted = [...rows].sort((a, b) =>
      new Date(a.call_time || a.created_at).getTime() -
      new Date(b.call_time || b.created_at).getTime()
    );

    const groups: any[] = [];
    const WINDOW_MS = 30 * 1000;

    for (const row of sorted) {
      if (row.call_type !== 'voice') {
        groups.push({ ...row, legs: [row] });
        continue;
      }
      const t = new Date(row.call_time || row.created_at).getTime();
      const propId = String(row.property_id || '');
      const rowFrom = (row.from_number || '').trim();

      // Group same-call legs together: same property, same caller (the
      // visitor's number is preserved on both inbound and outbound legs
      // because <Dial callerId> sets it on the outbound), and within a
      // short window. The 30s window prevents unrelated rapid-fire test
      // attempts from collapsing into a single row, while same-from
      // ensures we only group legs that actually share a caller.
      const open = !rowFrom ? null : groups.find(g => {
        if (g.call_type !== 'voice') return false;
        if (String(g.property_id || '') !== propId) return false;
        if ((g.from_number || '').trim() !== rowFrom) return false;
        return t - new Date(g.call_time || g.created_at).getTime() <= WINDOW_MS;
      });

      if (open) {
        open.legs.push(row);
        // Inbound (visitor) leg's duration encompasses the full wall
        // time — including IVR, ring, and the tenant's connected portion.
        // Outbound (tenant) leg's duration is a SUBSET. Use the longest
        // leg as the displayed total (don't sum).
        open.duration = Math.max(Number(open.duration || 0), Number(row.duration || 0));
        // Carry forward answered_at + tenant_talk_duration whichever leg
        // they came in on (legacy two-row calls — only the tenant leg
        // has answered_at set).
        if (!open.answered_at && row.answered_at) open.answered_at = row.answered_at;
        if (!open.tenant_talk_duration && row.tenant_talk_duration) open.tenant_talk_duration = row.tenant_talk_duration;
        // Promote to a "more connected" status if a child leg is better
        const statusRank = (s: string) => {
          const v = (s || '').toLowerCase();
          if (v === 'answered' || v === 'completed') return 3;
          if (v === 'unlocked') return 4;
          if (v === 'no_answer') return 1;
          return 0;
        };
        if (statusRank(row.status) > statusRank(open.status)) open.status = row.status;
        if (row.connected) open.connected = true;
        if (row.door_unlocked) open.door_unlocked = true;
      } else {
        groups.push({ ...row, legs: [row] });
      }
    }
    // Restore newest-first order
    return groups.sort((a, b) =>
      new Date(b.call_time || b.created_at).getTime() -
      new Date(a.call_time || a.created_at).getTime()
    );
  };

  const filtered = useMemo(() => {
    let result = calls;

    if (propertyFilter !== 'all') {
      result = result.filter(c => String(c.property_id) === String(propertyFilter));
    }

    if (tenantFilter !== 'all') {
      result = result.filter(c => (c.tenant_name || '—') === tenantFilter);
    }

    if (typeFilter !== 'all') {
      result = result.filter(c => (c.call_type || 'voice') === typeFilter);
    }

    if (dateFilter !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const weekAgo = new Date(today);
      weekAgo.setDate(weekAgo.getDate() - 7);
      const monthAgo = new Date(today);
      monthAgo.setMonth(monthAgo.getMonth() - 1);

      result = result.filter((c: any) => {
        const callTime = new Date(c.call_time || c.created_at);
        if (dateFilter === 'today') return callTime >= today;
        if (dateFilter === 'week') return callTime >= weekAgo;
        if (dateFilter === 'month') return callTime >= monthAgo;
        return true;
      });
    }

    // Merge voice-call legs BEFORE applying status filter so a merged
    // group's promoted status is respected.
    result = mergeVoiceLegs(result);

    if (statusFilter !== 'all') {
      // Each filter bucket maps to MULTIPLE underlying statuses — video calls
      // resolve to "completed"/"unlocked", phone calls to "answered", and
      // both should count under "Answered". Same idea for missed / no_answer.
      const buckets: Record<Exclude<StatusFilter, 'all'>, string[]> = {
        answered:  ['answered', 'completed', 'unlocked'],
        // "No Answer" rolls into Missed — same outcome from the manager's view.
        missed:    ['missed', 'unanswered', 'failed', 'no_answer', 'no answer', 'timeout'],
      };
      const allowed = buckets[statusFilter];
      result = result.filter(c => allowed.includes((c.status || '').toLowerCase()));
    }

    // Free-text search across key call fields
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      result = result.filter(c => {
        const hay = [c.caller, c.property_name, c.tenant_name, c.unit_number, c.status, c.call_type, c.from_number, c.to_number]
          .filter(Boolean).join(' ').toLowerCase();
        return hay.includes(q);
      });
    }

    return result;
  }, [calls, propertyFilter, dateFilter, statusFilter, tenantFilter, typeFilter, searchQuery]);

  // Tenant options derived from currently-loaded calls, scoped to the
  // selected property so cross-property names don't pollute the dropdown.
  const tenantOptions = useMemo(() => {
    const seen = new Set<string>();
    calls.forEach((c) => {
      if (propertyFilter !== 'all' && String(c.property_id) !== String(propertyFilter)) return;
      const name = (c.tenant_name || '').trim();
      if (name && name !== '—') seen.add(name);
    });
    return Array.from(seen).sort((a, b) => a.localeCompare(b));
  }, [calls, propertyFilter]);

  const totalCalls = filtered.length;
  const answeredCalls = filtered.filter(c => ['answered', 'completed', 'unlocked'].includes((c.status || '').toLowerCase()));
  const answeredRate = totalCalls > 0 ? ((answeredCalls.length / totalCalls) * 100).toFixed(1) : '0.0';
  // Each merged group's `duration` already sums all legs, so the totals
  // and per-call averages count voice minutes from both legs combined.
  const totalDuration = answeredCalls.reduce((sum, c) => sum + (c.duration || 0), 0);
  const avgDuration = answeredCalls.length > 0 ? Math.round(totalDuration / answeredCalls.length) : 0;
  const doorUnlocks = filtered.filter(c => c.door_unlocked).length;
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 25;
  // Reset to first page when the underlying filter set changes so the
  // user doesn't end up on a page that no longer exists.
  useEffect(() => { setPage(0); }, [propertyFilter, dateFilter, statusFilter, tenantFilter, typeFilter, presetTenantId, searchQuery]);
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = useMemo(
    () => filtered.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE),
    [filtered, page]
  );

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const exportCSV = () => {
    const cols = ['Date/Time', 'Type', 'Caller', 'Property', 'Unit', 'Tenant', 'Status', 'Duration (sec)', 'Tenant Talk (sec)', 'Door Unlocked', 'Answered At', 'Ended At'];
    const rows = filtered.map((c: any) => [
      c.call_time || c.created_at || '',
      c.call_type || 'voice',
      c.caller || '',
      c.property_name || '',
      c.unit_number !== '—' ? c.unit_number : '',
      c.tenant_name !== '—' ? c.tenant_name : '',
      c.status || '',
      c.duration || '',
      c.tenant_talk_duration || '',
      c.door_unlocked ? 'Yes' : 'No',
      c.answered_at || '',
      c.ended_at || '',
    ]);
    const csv = [cols, ...rows].map(r => r.map((v: any) => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `call-history-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getStatusBadge = (status: string) => {
    const normalized = (status || '').toLowerCase();
    if (normalized === 'answered' || normalized === 'unlocked' || normalized === 'completed') return <Badge status="success">{normalized === 'unlocked' ? 'Unlocked' : 'Answered'}</Badge>;
    if (normalized === 'missed' || normalized === 'unanswered') return <Badge status="error">Missed</Badge>;
    // No Answer / timeout collapse into Missed (same outcome).
    if (normalized === 'no_answer' || normalized === 'no answer' || normalized === 'timeout') return <Badge status="error">Missed</Badge>;
    if (normalized === 'ringing' || normalized === 'in_progress') return <Badge status="info">{status}</Badge>;
    return <Badge>{status || 'Unknown'}</Badge>;
  };

  // properties[] is indexed under both `id` and `uuid` keys, so naively
  // calling Object.values gives every property twice. Dedupe by uuid|id
  // before sorting so the dropdown lists each property exactly once.
  const propertyOptions = Object.values(
    Object.values(properties).reduce<Record<string, any>>((acc, p: any) => {
      const k = String(p.uuid || p.id);
      if (k && !acc[k]) acc[k] = p;
      return acc;
    }, {}),
  ).sort((a: any, b: any) => (a.name || '').localeCompare(b.name || ''));

  const getCallTypeIcon = (type: string) => {
    switch (type) {
      case 'video': return <Badge color="purple">Video</Badge>;
      case 'kiosk': return <Badge color="blue">Kiosk</Badge>;
      default: return <Badge color="gray">Phone</Badge>;
    }
  };

  const formatTimestamp = (ts: string) => {
    if (!ts) return '—';
    const d = new Date(ts);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
  };

  const columns = [
    {
      key: 'call_time',
      header: 'Date/Time',
      render: (call: any) => <span className="text-xs text-gray-600">{formatTimestamp(call.call_time || call.created_at)}</span>
    },
    {
      key: 'call_type',
      header: 'Type',
      render: (call: any) => getCallTypeIcon(call.call_type)
    },
    {
      key: 'caller',
      header: 'Caller',
      render: (call: any) => <span className="text-gray-700">{call.caller}</span>
    },
    {
      key: 'property_name',
      header: 'Property',
      render: (call: any) => <span className="font-medium">{call.property_name}</span>
    },
    {
      key: 'unit_number',
      header: 'Unit',
      render: (call: any) => call.unit_number !== '—' ? `#${call.unit_number}` : '—'
    },
    {
      key: 'tenant_name',
      header: 'Tenant',
      render: (call: any) => call.tenant_name
    },
    {
      key: 'connected',
      header: 'Connected',
      render: (call: any) => call.connected
        ? <span className="text-green-600 font-medium text-xs">Connected</span>
        : <span className="text-red-500 text-xs">Not Connected</span>
    },
    {
      key: 'status',
      header: 'Status',
      render: (call: any) => getStatusBadge(call.status)
    },
    {
      key: 'duration',
      header: 'Duration',
      render: (call: any) => {
        if (!call.duration) return '—';
        const total = formatDuration(call.duration);
        if (call.tenant_talk_duration && call.tenant_talk_duration > 0) {
          return (
            <div className="leading-tight">
              <div className="text-sm">{total}</div>
              <div className="text-[11px] text-gray-500">Tenant: {formatDuration(call.tenant_talk_duration)}</div>
            </div>
          );
        }
        return total;
      }
    },
    {
      key: 'door_unlocked',
      header: 'Doors',
      render: (call: any) => {
        const assigned: string[] = Array.isArray(call.assigned_door_names) ? call.assigned_door_names : [];
        const unlocked: string[] = Array.isArray(call.unlocked_door_names) ? call.unlocked_door_names : [];
        if (assigned.length === 0) {
          return call.door_unlocked
            ? <span className="text-green-600 text-lg">✓</span>
            : <span className="text-gray-400">—</span>;
        }
        const unlockedSet = new Set(unlocked);
        return (
          <div className="flex flex-wrap gap-1">
            {assigned.map((name) => {
              const opened = unlockedSet.has(name);
              return (
                <span
                  key={name}
                  className={
                    opened
                      ? 'text-[11px] px-1.5 py-0.5 rounded bg-green-50 text-green-700 border border-green-200'
                      : 'text-[11px] px-1.5 py-0.5 rounded bg-gray-50 text-gray-500 border border-gray-200'
                  }
                >
                  {opened ? '🔓 ' : ''}{name}
                </span>
              );
            })}
          </div>
        );
      },
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
      </div>
    );
  }

  return (
    <div className={hidePageHeader ? 'embedded-call-history' : 'shadow-list-page prototype-page history-page'}>
      {!hidePageHeader && (
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Call History</h1>
            <p className="text-gray-500 mt-1">Video intercom call records and analytics</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={exportCSV}
              disabled={filtered.length === 0}
              className="flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
            <button
              type="button"
              onClick={() => load()}
              disabled={loading}
              className="flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
      )}

      {!hidePageHeader && <CountBand value={filtered.length} detail="Calls in the current accessible portfolio" />}

      {/* Summary Cards */}
      <div className="legacy-summary-cards grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Calls</p>
              <p className="text-2xl font-bold text-gray-900">{totalCalls}</p>
            </div>
            <div className="w-12 h-12 bg-brand-100 rounded-lg flex items-center justify-center">
              <Phone className="w-6 h-6 text-brand-600" />
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Answered Rate</p>
              <p className="text-2xl font-bold text-green-600">{answeredRate}%</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Avg Duration</p>
              <p className="text-2xl font-bold text-emerald-600">{formatDuration(avgDuration)}</p>
            </div>
            <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
              <Clock className="w-6 h-6 text-emerald-600" />
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Door Unlocks</p>
              <p className="text-2xl font-bold text-purple-600">{doorUnlocks}</p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <Unlock className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </Card>
      </div>

      {/* Filters + Table */}
      <Card padding={false}>
        <div className="prototype-toolbar history-toolbar p-4 border-b border-gray-100">
          <div className="flex flex-wrap items-center gap-4">
            {/* Free-text search */}
            <div className="relative flex items-center">
              <Search className="absolute left-2.5 w-4 h-4 text-gray-400 pointer-events-none" />
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search caller, property, tenant, unit…"
                className="pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 w-64"
              />
            </div>
            {/* Property Filter — hidden when this view is embedded with
                a preset property (e.g. Property Detail's Calls tab). */}
            {!presetPropertyId && (
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700">Property:</label>
                <select
                  value={propertyFilter}
                  onChange={(e) => setPropertyFilter(e.target.value === 'all' ? 'all' : e.target.value)}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500"
                >
                  <option value="all">All Properties</option>
                  {propertyOptions.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Tenant Filter — derived from loaded calls so the list is
                always relevant to the visible scope. Hidden when embedding
                inside a tenant-scoped view (already filtered server-side). */}
            {!presetTenantId && (
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700">Tenant:</label>
                <select
                  value={tenantFilter}
                  onChange={(e) => setTenantFilter(e.target.value)}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500"
                >
                  <option value="all">All Tenants</option>
                  {tenantOptions.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Type Filter */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">Type:</label>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value as TypeFilter)}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500"
              >
                <option value="all">All Types</option>
                <option value="voice">Phone</option>
                <option value="video">Video</option>
                <option value="kiosk">Kiosk</option>
              </select>
            </div>

            {/* Date Filter */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setDateFilter('today')}
                className={`px-3 py-2 text-sm rounded-lg ${
                  dateFilter === 'today'
                    ? 'bg-brand-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Today
              </button>
              <button
                onClick={() => setDateFilter('week')}
                className={`px-3 py-2 text-sm rounded-lg ${
                  dateFilter === 'week'
                    ? 'bg-brand-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                This Week
              </button>
              <button
                onClick={() => setDateFilter('month')}
                className={`px-3 py-2 text-sm rounded-lg ${
                  dateFilter === 'month'
                    ? 'bg-brand-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                This Month
              </button>
              <button
                onClick={() => setDateFilter('all')}
                className={`px-3 py-2 text-sm rounded-lg ${
                  dateFilter === 'all'
                    ? 'bg-brand-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All
              </button>
            </div>

            {/* Status Filter */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">Status:</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500"
              >
                <option value="all">All</option>
                <option value="answered">Answered</option>
                <option value="missed">Missed</option>
              </select>
            </div>
          </div>
        </div>
        {/* Mobile cards */}
        <div className="md:hidden p-3 space-y-2">
          {paged.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-6 text-center text-sm text-gray-400">No calls recorded yet</div>
          ) : paged.map((call: any) => {
            const status = (call.status || 'unknown').toLowerCase();
            const sc = ['answered','completed','unlocked'].includes(status) ? 'bg-green-100 text-green-700' : ['missed','unanswered','no_answer','no answer','timeout'].includes(status) ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600';
            const typeBadge = call.call_type === 'video' ? 'bg-purple-100 text-purple-700' : call.call_type === 'kiosk' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-700';
            // Visitor snapshot — same lookup pattern as the desktop expanded row.
            // Only video calls capture snapshots; guard against truncated /
            // placeholder data URLs by requiring a real data:image/ prefix
            // and a non-trivial length.
            let snap: string | null = call.call_type === 'video'
              ? (call.snapshot_url || (call.legs ?? []).map((l: any) => l.snapshot_url).find(Boolean) || null)
              : null;
            if (snap && (typeof snap !== 'string' || snap.length < 1000 || !snap.startsWith('data:image/'))) {
              snap = null;
            }
            return (
              <div key={call.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="flex gap-3 p-3">
                  {/* Visitor snapshot thumbnail (video only, when captured) */}
                  {snap ? (
                    <img
                      src={snap}
                      alt="Visitor"
                      className="w-16 h-20 rounded-lg object-cover border border-gray-200 flex-shrink-0 bg-gray-100"
                    />
                  ) : null}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <div className="min-w-0">
                        <p className="text-xs text-gray-500">{formatTimestamp(call.call_time || call.created_at)}</p>
                        <p className="font-semibold text-gray-900 truncate">{call.caller}</p>
                      </div>
                      <div className="flex flex-col items-end gap-1 flex-shrink-0">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium capitalize ${typeBadge}`}>{call.call_type === 'video' ? 'Video' : call.call_type === 'kiosk' ? 'Kiosk' : 'Phone'}</span>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${sc}`}>{status === 'unlocked' ? 'Unlocked' : status}</span>
                      </div>
                    </div>
                    <div className="text-xs text-gray-600 grid grid-cols-2 gap-1 mt-1">
                      <p>📍 {call.property_name}</p>
                      <p>🏠 {call.unit_number !== '—' ? `Unit ${call.unit_number}` : '—'}</p>
                      <p className="truncate">👤 {call.tenant_name}</p>
                      <p>⏱ {call.duration ? formatDuration(call.duration) : '—'}{call.door_unlocked ? ' · 🔓' : ''}</p>
                    </div>
                    <div className="mobile-card-actions">
                      <button onClick={() => setExpandedId(expandedId === call.id ? null : call.id)} className="row-open">
                        {expandedId === call.id ? 'Hide details' : 'Details'} <span aria-hidden="true">→</span>
                      </button>
                    </div>
                  </div>
                </div>
                {expandedId === call.id && (
                  <div className="border-t border-gray-200 px-3 py-3 text-xs text-gray-600 grid grid-cols-2 gap-2">
                    <p><strong className="block text-gray-400 uppercase text-[10px]">Connected</strong>{call.connected ? 'Yes' : 'No'}</p>
                    <p><strong className="block text-gray-400 uppercase text-[10px]">Doors</strong>{call.door_unlocked ? 'Unlocked' : 'No unlock'}</p>
                    <p><strong className="block text-gray-400 uppercase text-[10px]">Started</strong>{formatTimestamp(call.created_at || call.call_time)}</p>
                    <p><strong className="block text-gray-400 uppercase text-[10px]">Ended</strong>{call.ended_at ? formatTimestamp(call.ended_at) : '—'}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className="hidden md:block">
        <Table
          columns={columns}
          data={paged}
          emptyMessage="No calls recorded yet"
          onRowClick={(call: any) => {
            // All call types can expand to show the detail/timeline panel.
            // Video calls additionally show the visitor snapshot.
            setExpandedId(expandedId === call.id ? null : call.id);
          }}
          expandedRow={(call: any) => {
            if (expandedId !== call.id) return null;

            // Video calls show the visitor snapshot captured at call start.
            let snap = call.snapshot_url
              || (call.legs ?? []).map((l: any) => l.snapshot_url).find(Boolean)
              || null;
            // Anything under ~1KB can't be a real JPEG — guard against
            // truncated uploads / placeholder test data so the image
            // doesn't render as a broken-icon.
            if (snap && (typeof snap !== 'string' || snap.length < 1000 || !snap.startsWith('data:image/'))) {
              snap = null;
            }

            // Build call timeline from available timestamps
            const timelineEntries: { label: string; ts: string | null }[] = [
              { label: 'Created', ts: call.created_at || call.call_time || null },
              { label: 'Invited at', ts: call.invited_at || null },
              { label: 'Ringing at', ts: call.ringing_at || null },
              { label: 'Answered at', ts: call.answered_at || null },
              { label: 'Ended at', ts: call.ended_at || null },
            ].filter(e => e.ts);

            return (
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
                <div className="flex items-start gap-6 flex-wrap">
                  {snap && (
                    <img src={snap} alt="Visitor snapshot"
                      className="rounded-lg border border-gray-200 max-h-48"
                      style={{ maxWidth: '320px' }} />
                  )}
                  <div className="flex-1 min-w-0 space-y-3">
                    {/* Call detail grid */}
                    <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm text-gray-700">
                      <div><span className="font-semibold text-gray-500 text-xs uppercase">Visitor</span><br />{call.caller || '—'}</div>
                      <div><span className="font-semibold text-gray-500 text-xs uppercase">Tenant</span><br />{call.tenant_name || '—'}</div>
                      <div><span className="font-semibold text-gray-500 text-xs uppercase">Unit</span><br />{call.unit_number !== '—' ? `#${call.unit_number}` : '—'}</div>
                      <div><span className="font-semibold text-gray-500 text-xs uppercase">Property</span><br />{call.property_name || '—'}</div>
                      <div><span className="font-semibold text-gray-500 text-xs uppercase">Total Duration</span><br />{call.duration ? formatDuration(call.duration) : '—'}</div>
                      {call.tenant_talk_duration > 0 && (
                        <div><span className="font-semibold text-gray-500 text-xs uppercase">Tenant Talk</span><br />{formatDuration(call.tenant_talk_duration)}</div>
                      )}
                    </div>

                    {/* Call timeline */}
                    {timelineEntries.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase mb-1.5">Call Timeline</p>
                        <div className="flex items-start gap-0">
                          {timelineEntries.map((entry, idx) => {
                            const prev = timelineEntries[idx - 1];
                            const lagMs = prev?.ts ? new Date(entry.ts!).getTime() - new Date(prev.ts).getTime() : null;
                            const lagLabel = lagMs !== null && lagMs >= 0
                              ? lagMs < 1000 ? `+${lagMs}ms` : `+${(lagMs / 1000).toFixed(1)}s`
                              : null;
                            return (
                              <div key={entry.label} className="flex items-center">
                                {idx > 0 && (
                                  <div className="flex flex-col items-center mx-1">
                                    <div className="h-px w-6 bg-gray-300" />
                                    {lagLabel && <span className="text-[9px] text-gray-400 whitespace-nowrap">{lagLabel}</span>}
                                  </div>
                                )}
                                <div className="flex flex-col items-center">
                                  <div className="w-2 h-2 rounded-full bg-brand-500 flex-shrink-0" />
                                  <p className="text-[10px] text-gray-500 mt-0.5 whitespace-nowrap">{entry.label}</p>
                                  <p className="text-[9px] text-gray-400 whitespace-nowrap">
                                    {new Date(entry.ts!).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true })}
                                  </p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Disconnect / legs info */}
                    {call.disconnect_reason && (
                      <p className="text-xs text-gray-500">Disconnect: <span className="font-mono">{call.disconnect_reason}</span></p>
                    )}
                    {!snap && <p className="text-xs text-gray-400">No snapshot captured for this call.</p>}
                  </div>
                </div>
              </div>
            );
          }}
        />
        </div>
        {filtered.length > PAGE_SIZE && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 text-sm text-gray-600">
            <span>
              Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, filtered.length)} of {filtered.length}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={page === 0}
                onClick={() => setPage(p => Math.max(0, p - 1))}
                className="px-3 py-1.5 rounded-md border border-gray-200 disabled:opacity-40 hover:bg-gray-50"
              >Previous</button>
              <span className="px-2">Page {page + 1} of {pageCount}</span>
              <button
                type="button"
                disabled={page >= pageCount - 1}
                onClick={() => setPage(p => Math.min(pageCount - 1, p + 1))}
                className="px-3 py-1.5 rounded-md border border-gray-200 disabled:opacity-40 hover:bg-gray-50"
              >Next</button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};
